import {
  ApiSuccessResponse,
  ExpenseExportBody,
  GraphBody,
  IncomeExportBody,
} from "../../@types";
import { apiSlice } from "../../app/api/apiSlice";
import {
  ExpenseHeads,
  ExpenseHeadsSearchRequest,
  ExpenseHeadsSearchResponse,
  ExpenseItem,
  ExpenseItemSearchRequest,
  ExpenseItemSearchResponse,
  ExpenseItemStats,
  FinancialSummary,
  GraphResponse,
} from "./@types";

export const expenseApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getIncomeExpenseItems: builder.mutation<
      ApiSuccessResponse<ExpenseItemSearchResponse>,
      ExpenseItemSearchRequest
    >({
      query: (data) => ({
        url: "/v1/incomeandexpense/income/search",
        method: "POST",
        body: { ...data },
      }),
    }),
    getExpenseItems: builder.mutation<
      ApiSuccessResponse<ExpenseItemSearchResponse>,
      ExpenseItemSearchRequest
    >({
      query: (data) => ({
        url: "/v1/incomeandexpense/expense/search",
        method: "POST",
        body: { ...data },
      }),
    }),
    getExpenseItemStats: builder.query<
      ApiSuccessResponse<ExpenseItemStats>,
      void
    >({
      query: () => ({
        url: "/v1/fuellog/stat",
        method: "GET",
      }),
    }),
    getExpenseItemById: builder.query<ApiSuccessResponse<ExpenseItem>, string>({
      query: (id) => ({
        url: `/v1/incomeandexpense/income/${id}`,
        method: "GET",
      }),
    }),
    getFinanceSummary: builder.query<
      ApiSuccessResponse<FinancialSummary>,
      string
    >({
      query: (section) => ({
        url: `/v1/incomeandexpense/financialsummary/${section}`,
        method: "GET",
      }),
    }),
    getCompanyFinanceSummary: builder.query<
      ApiSuccessResponse<FinancialSummary>,
      void
    >({
      query: () => ({
        url: `/v1/incomeandexpense/financialsummary`,
        method: "POST",
        body: {},
      }),
    }),
    getIncomeGraphData: builder.mutation<
      ApiSuccessResponse<GraphResponse>,
      GraphBody
    >({
      query: (data) => ({
        url: "/v1/incomeandexpense/incomes/graph",
        method: "POST",
        body: { ...data },
      }),
    }),
    getExpenseGraphData: builder.mutation<
      ApiSuccessResponse<GraphResponse>,
      GraphBody
    >({
      query: (data) => ({
        url: "/v1/incomeandexpense/expenses/graph",
        method: "POST",
        body: { ...data },
      }),
    }),
    exportIncome: builder.mutation<Blob, IncomeExportBody>({
      query: (data) => ({
        url: "/v1/incomeandexpense/export/income",
        method: "POST",
        body: data,
        responseHandler: (response) => response.blob(),
      }),
    }),
    exportExpense: builder.mutation<Blob, ExpenseExportBody>({
      query: (data) => ({
        url: "/v1/incomeandexpense/export/expense",
        method: "POST",
        body: data,
        responseHandler: (response) => response.blob(),
      }),
    }),
    createExpenseHead: builder.mutation<void, Partial<ExpenseHeads>>({
      query: (data) => ({
        url: "/v1/expenseheads/expense-head",
        method: "POST",
        body: { ...data },
      }),
    }),
    updateExpenseHead: builder.mutation<void, Partial<ExpenseHeads>>({
      query: (data) => ({
        url: "/v1/expenseheads/expense-head",
        method: "POST",
        body: { ...data },
      }),
    }),
    deleteExpenseHead: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/expenseheads/expense-head/${id}`,
        method: "DELETE",
      }),
    }),
    getAllExpenseHeads: builder.mutation<
      ApiSuccessResponse<ExpenseHeadsSearchResponse>,
      ExpenseHeadsSearchRequest
    >({
      query: (data) => ({
        url: "/v1/expenseheads/expense-head/by-company",
        method: "POST",
        body: { ...data },
      }),
    }),
  }),
});

export const {
  useGetIncomeExpenseItemsMutation,
  useGetExpenseItemsMutation,
  useGetExpenseItemStatsQuery,
  useGetExpenseItemByIdQuery,
  useGetFinanceSummaryQuery,
  useGetCompanyFinanceSummaryQuery,
  useGetIncomeGraphDataMutation,
  useGetExpenseGraphDataMutation,
  useExportIncomeMutation,
  useExportExpenseMutation,
  useCreateExpenseHeadMutation,
  useUpdateExpenseHeadMutation,
  useDeleteExpenseHeadMutation,
  useGetAllExpenseHeadsMutation,
} = expenseApiSlice;
